import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TimerIcon from '@mui/icons-material/Timer';
import { toast } from 'react-toastify';

import {
  modelLabAssetApi,
  modelLabDatasetVersionApi,
  modelLabFeatureSetApi,
  modelLabTrainingJobApi,
  modelLabVersionApi,
} from '../../services/api';
import useModelLabTrainingJobsSocket from '../../hooks/useModelLabTrainingJobsSocket';
import type { ModelLabTrainingJob, ModelLabTrainingJobCreate } from '../../types';
import { ConfirmDialog, ErrorAlert, LoadingSpinner, PageHeader } from '../common';
import {
  formatMetricSummary,
  getApiErrorMessage,
  parseCommaList,
  trainingStatusColor,
} from './utils';
import { ConfusionMatrix, ROCCurve, PRCurve, FeatureImportance, LossChart } from './charts';
import { ComputeSpecSelector } from './ComputeSpecSelector';
import {
  TrainingJobDetailsDialog,
  TrainingJobProgressSummary,
} from './TrainingJobMonitor';

interface TrainingJobFormState {
  modelAssetId: string;
  datasetVersionId: string;
  featureSetId: string;
  targetColumn: string;
  featureColumns: string;
  executor: string;
  computeSpecId: number | null;
  testSize: string;
  randomState: string;
  cvFolds: string;
  autoLaunch: boolean;
}

const initialTrainingForm: TrainingJobFormState = {
  modelAssetId: '',
  datasetVersionId: '',
  featureSetId: '',
  targetColumn: 'target',
  featureColumns: '',
  executor: 'cpu',
  computeSpecId: null,
  testSize: '0.25',
  randomState: '42',
  cvFolds: '0',
  autoLaunch: true,
};

const computeModeLabel = (job: ModelLabTrainingJob) => {
  const runtime = (job as any).runtime_spec || {};
  const progress = (job as any).progress || {};
  if (runtime.compute_mode === 'remote_vm') {
    return progress.runner_simulation === 'local_docker'
      ? 'Remote VM (local simulation)'
      : 'Remote VM';
  }
  if (runtime.compute_mode === 'local') {
    return 'Legacy local';
  }
  return String(runtime.compute_mode || '');
};

/** Minimal label lookup for displaying algorithm names in read-only summaries */
const ALGORITHM_LABELS: Record<string, string> = {
  logistic_regression: 'Logistic Regression', naive_bayes: 'Naive Bayes',
  sgd_classifier: 'SGD Classifier', linear_regression: 'Linear Regression',
  ridge: 'Ridge', lasso: 'Lasso', elastic_net: 'ElasticNet',
  sgd_regressor: 'SGD Regressor', random_forest: 'Random Forest',
  gradient_boosting: 'Gradient Boosting', extra_trees: 'Extra Trees',
  adaboost: 'AdaBoost', decision_tree: 'Decision Tree', knn: 'KNN',
  svm: 'SVM', mlp: 'Neural Network (MLP)', xgboost: 'XGBoost',
  lightgbm: 'LightGBM', lstm: 'LSTM', bilstm: 'BiLSTM', gru: 'GRU',
  xlstm: 'xLSTM', transformer: 'Transformer Encoder',
  transformer_encoder: 'Transformer Encoder',
  temporal_fusion_transformer: 'TFT', informer: 'Informer',
  patch_tst: 'PatchTST', cnn_1d: '1D CNN', tcn: 'TCN',
  conv_lstm: 'ConvLSTM', wavenet: 'WaveNet', nbeats: 'N-BEATS',
  autoencoder: 'Autoencoder', vae: 'VAE',
  attention_network: 'Multi-Head Attention Net',
};

/** Extract preprocessing config from an architecture's preprocessing array */
const extractPreprocessing = (archPreprocessing: any[]) => {
  const preprocessing: Record<string, any> = {};
  (archPreprocessing || []).forEach((step: any) => {
    const t = step.type || '';
    if (t.includes('scaler')) preprocessing.scaler = t.replace('_scaler', '');
    else if (t.startsWith('imputer_')) preprocessing.imputer = t.replace('imputer_', '');
    else if (['pca', 'select_k_best', 'variance_threshold'].includes(t)) {
      preprocessing.feature_selection = t;
      if (step.params?.n_components) preprocessing.feature_selection_k = step.params.n_components;
      if (step.params?.k) preprocessing.feature_selection_k = step.params.k;
    }
  });
  return preprocessing;
};

/** Build the config payload from an asset's architecture */
const buildArchitectureConfig = (arch: any) => {
  if (!arch) return {};
  const modelType = arch.model_type || 'single';
  const preprocessing = extractPreprocessing(arch.preprocessing);
  return {
    algorithm: arch.single_estimator?.algorithm || 'random_forest',
    hyperparameters: arch.single_estimator?.params || {},
    ...(Object.keys(preprocessing).length > 0 ? { preprocessing } : {}),
    model_type: modelType,
    ...(modelType === 'voting' ? {
      voting_strategy: arch.voting_strategy || 'soft',
      ensemble_models: (arch.base_estimators || []).map((e: any) => ({
        algorithm: e.algorithm,
        hyperparameters: e.params || {},
      })),
    } : {}),
    ...(modelType === 'stacking' ? {
      ensemble_models: (arch.base_estimators || []).map((e: any) => ({
        algorithm: e.algorithm,
        hyperparameters: e.params || {},
      })),
      meta_learner: {
        algorithm: arch.meta_learner?.algorithm || 'logistic_regression',
        hyperparameters: arch.meta_learner?.params || {},
      },
    } : {}),
  };
};

const ModelLabTrainingJobsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingJobId, setEditingJobId] = useState<number | null>(null);
  const [jobToDelete, setJobToDelete] = useState<{ id: number; name: string } | null>(null);
  const [jobForm, setJobForm] = useState<TrainingJobFormState>(initialTrainingForm);
  const [logDialogJobId, setLogDialogJobId] = useState<number | null>(null);
  const [inspectTab, setInspectTab] = useState(0);
  const [search, setSearch] = useState('');
  const queryAssetId = searchParams.get('asset') || '';
  const jobsQueryKey = useMemo(
    () => ['model-lab-training-jobs', search, queryAssetId] as const,
    [search, queryAssetId],
  );
  const jobsSocketEnabled = !search;
  useModelLabTrainingJobsSocket({
    modelAssetId: queryAssetId ? Number(queryAssetId) : 'all',
    enabled: jobsSocketEnabled,
    queryKey: jobsQueryKey,
    pageSize: 50,
  });

  const jobsQuery = useQuery({
    queryKey: jobsQueryKey,
    queryFn: () => modelLabTrainingJobApi.listSummary({
      page_size: 50,
      search: search || undefined,
      model_asset: queryAssetId ? Number(queryAssetId) : undefined,
      ordering: '-created_at',
    }),
    enabled: true,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: !jobsSocketEnabled,
    staleTime: jobsSocketEnabled ? 300000 : 30000,
  });

  const assetsQuery = useQuery({
    queryKey: ['model-lab-assets-for-training'],
    queryFn: () => modelLabAssetApi.list({ page_size: 100 }),
  });

  const datasetVersionsQuery = useQuery({
    queryKey: ['model-lab-dataset-versions-for-training'],
    queryFn: () => modelLabDatasetVersionApi.list({ page_size: 100 }),
  });

  const featureSetsQuery = useQuery({
    queryKey: ['model-lab-feature-sets-for-training'],
    queryFn: () => modelLabFeatureSetApi.list(),
  });

  const selectedAsset = (assetsQuery.data?.results || []).find((a) => String(a.id) === jobForm.modelAssetId);

  useEffect(() => {
    if (!queryAssetId || assetsQuery.isLoading) return;
    const asset = (assetsQuery.data?.results || []).find((a) => String(a.id) === queryAssetId);
    if (!asset) return;
    const featureColumns = asset.runtime_spec?.authoring?.no_code?.feature_columns;

    setJobForm((current) => ({
      ...current,
      modelAssetId: queryAssetId,
      targetColumn: asset.runtime_spec?.authoring?.no_code?.target_name || current.targetColumn,
      featureColumns: Array.isArray(featureColumns) ? featureColumns.join(', ') : String(featureColumns || current.featureColumns),
      executor: asset.runtime_spec?.executor || current.executor,
    }));

    if (searchParams.get('create') === '1') {
      setCreateOpen(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('create');
      setSearchParams(nextParams, { replace: true });
    }
  }, [assetsQuery.data, assetsQuery.isLoading, queryAssetId, searchParams, setSearchParams]);

  const createJobMutation = useMutation({
    mutationFn: async (form: TrainingJobFormState) => {
      const featureColumns = parseCommaList(form.featureColumns);
      const asset = (assetsQuery.data?.results || []).find((a) => String(a.id) === form.modelAssetId);
      const arch = asset?.runtime_spec?.authoring?.no_code?.architecture;
      const archConfig = buildArchitectureConfig(arch);

      const payload: ModelLabTrainingJobCreate = {
        model_asset: Number(form.modelAssetId),
        dataset_version: Number(form.datasetVersionId),
        runtime_spec: {
          authoring_mode: asset?.runtime_spec?.authoring_mode || 'no_code',
          executor: form.executor,
        },
        config: {
          target_column: form.targetColumn,
          test_size: Number(form.testSize),
          random_state: Number(form.randomState),
          ...(Number(form.cvFolds) > 0 ? { cv_folds: Number(form.cvFolds) } : {}),
          ...(featureColumns.length > 0 ? { feature_columns: featureColumns } : {}),
          ...archConfig,
        },
        ...(form.featureSetId ? { feature_set: Number(form.featureSetId) } : {}),
      };

      const job = await modelLabTrainingJobApi.create(payload);
      if (form.autoLaunch) {
        await modelLabTrainingJobApi.launch(job.id, form.computeSpecId);
      }
      return job;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-training-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      setCreateOpen(false);
      setJobForm(initialTrainingForm);
      toast.success('Training job created.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create training job.'));
    },
  });

  const launchMutation = useMutation({
    mutationFn: ({ id, computeSpecId }: { id: number; computeSpecId?: number | null }) =>
      modelLabTrainingJobApi.launch(id, computeSpecId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-training-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['model-lab-versions'] });
      toast.success('Training job launched.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to launch training job.'));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => modelLabTrainingJobApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-training-jobs'] });
      toast.success('Training job cancelled.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to cancel training job.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => modelLabTrainingJobApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-training-jobs'] });
      setJobToDelete(null);
      toast.success('Training job deleted.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete training job.'));
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: async ({ id, form }: { id: number; form: TrainingJobFormState }) => {
      const featureColumns = parseCommaList(form.featureColumns);
      const asset = (assetsQuery.data?.results || []).find((a) => String(a.id) === form.modelAssetId);
      const arch = asset?.runtime_spec?.authoring?.no_code?.architecture;
      const archConfig = buildArchitectureConfig(arch);

      return modelLabTrainingJobApi.update(id, {
        model_asset: Number(form.modelAssetId),
        dataset_version: Number(form.datasetVersionId),
        runtime_spec: {
          authoring_mode: asset?.runtime_spec?.authoring_mode || 'no_code',
          executor: form.executor,
        },
        config: {
          target_column: form.targetColumn,
          test_size: Number(form.testSize),
          random_state: Number(form.randomState),
          ...(Number(form.cvFolds) > 0 ? { cv_folds: Number(form.cvFolds) } : {}),
          ...(featureColumns.length > 0 ? { feature_columns: featureColumns } : {}),
          ...archConfig,
        },
        ...(form.featureSetId ? { feature_set: Number(form.featureSetId) } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-lab-training-jobs'] });
      setCreateOpen(false);
      setEditingJobId(null);
      setJobForm(initialTrainingForm);
      toast.success('Training job updated.');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update training job.'));
    },
  });

  const selectedJob = useMemo(
    () => (jobsQuery.data?.results || []).find((job) => job.id === logDialogJobId) || null,
    [jobsQuery.data?.results, logDialogJobId]
  );

  const telemetryQuery = useQuery({
    queryKey: ['model-lab-training-telemetry', logDialogJobId],
    queryFn: () => modelLabTrainingJobApi.telemetry(logDialogJobId!),
    enabled: false,
  });

  const error = jobsQuery.error || assetsQuery.error || datasetVersionsQuery.error;
  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  if (jobsQuery.isLoading || assetsQuery.isLoading || datasetVersionsQuery.isLoading) {
    return <LoadingSpinner message="Loading training jobs..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Training Jobs"
        breadcrumbs={[
          { label: 'Model Lab', to: '/ai/model-lab' },
          { label: 'Training Jobs' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              New Job
            </Button>
          </Stack>
        }
      />

      <Stack spacing={2}>
        <Alert severity="info">
          Training supports ML (scikit-learn) and DL (PyTorch) model assets.
          Define the architecture in Model Registry, then create and launch a training job here.
        </Alert>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Model Asset</TableCell>
                <TableCell>Dataset Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Metrics</TableCell>
                <TableCell>Registered Version</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(jobsQuery.data?.results || []).map((job) => (
                <TableRow
                  key={job.id}
                  hover
                  onClick={() => setLogDialogJobId(job.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{job.model_asset_name}</TableCell>
                  <TableCell>{job.dataset_version_name}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Chip
                        size="small"
                        label={job.status}
                        color={trainingStatusColor(job.status)}
                      />
                      <TrainingJobProgressSummary job={job} compact />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {formatMetricSummary(job.metrics).map((metric) => (
                        <Chip key={metric} size="small" label={metric} variant="outlined" />
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {job.generated_model_version_id
                      ? `Version #${job.generated_model_version_id}`
                      : 'Not registered yet'}
                  </TableCell>
                  <TableCell>
                    {job.completed_at
                      ? new Date(job.completed_at).toLocaleString()
                      : 'Pending'}
                  </TableCell>
                  <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                    {(job.status === 'DRAFT' ||
                      job.status === 'FAILED' ||
                      job.status === 'CANCELLED') && (() => {
                      return (
                        <Tooltip title="Launch">
                          <IconButton
                            color="primary"
                            onClick={() => launchMutation.mutate({
                              id: job.id,
                              computeSpecId: ((job as any).runtime_spec || {}).compute_spec_id ?? null,
                            })}
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      );
                    })()}
                    {(job.status === 'QUEUED' || job.status === 'RUNNING') && (
                      <IconButton
                        color="warning"
                        onClick={() => cancelMutation.mutate(job.id)}
                      >
                        <StopIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => setLogDialogJobId(job.id)}>
                      <DescriptionIcon />
                    </IconButton>
                    <Tooltip title="Clone job">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const config = job.config || {};
                          const rs = (job as any).runtime_spec || {};
                          const assetExists = (assetsQuery.data?.results || []).some((a) => a.id === job.model_asset);
                          const dvExists = (datasetVersionsQuery.data?.results || []).some((v) => v.id === job.dataset_version);
                          setJobForm({
                            modelAssetId: assetExists ? String(job.model_asset || '') : '',
                            datasetVersionId: dvExists ? String(job.dataset_version || '') : '',
                            featureSetId: String(job.feature_set || ''),
                            targetColumn: config.target_column || 'target',
                            featureColumns: (config.feature_columns || []).join(', '),
                            executor: config.executor || rs.executor || 'cpu',
                            computeSpecId: rs.compute_spec_id ?? null,
                            testSize: String(config.test_size ?? 0.25),
                            randomState: String(config.random_state ?? 42),
                            cvFolds: String(config.cv_folds ?? 0),
                            autoLaunch: false,
                          });
                          setCreateOpen(true);
                        }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {(job.status === 'DRAFT' || job.status === 'FAILED' || job.status === 'CANCELLED') && (
                      <Tooltip title="Edit job">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const config = job.config || {};
                            const rs = (job as any).runtime_spec || {};
                            setEditingJobId(job.id);
                            setJobForm({
                              modelAssetId: String(job.model_asset || ''),
                              datasetVersionId: String(job.dataset_version || ''),
                              featureSetId: String(job.feature_set || ''),
                              targetColumn: config.target_column || 'target',
                              featureColumns: (config.feature_columns || []).join(', '),
                              executor: config.executor || rs.executor || 'cpu',
                              computeSpecId: rs.compute_spec_id ?? null,
                              testSize: String(config.test_size ?? 0.25),
                              randomState: String(config.random_state ?? 42),
                              cvFolds: String(config.cv_folds ?? 0),
                              autoLaunch: false,
                            });
                            setCreateOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete job">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setJobToDelete({ id: job.id, name: job.model_asset_name || `Job #${job.id}` })}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {(jobsQuery.data?.results || []).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No training jobs yet. Create one to generate your first model version.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditingJobId(null); setJobForm(initialTrainingForm); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingJobId ? 'Edit Training Job' : 'Create Training Job'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* ── Section: Data ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Data Sources
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Model asset"
                value={jobForm.modelAssetId}
                onChange={(event) => {
                  const assetId = event.target.value;
                  const asset = (assetsQuery.data?.results || []).find((a) => String(a.id) === assetId);
                  const noCode = asset?.runtime_spec?.authoring?.no_code;
                  setJobForm((current) => ({
                    ...current,
                    modelAssetId: assetId,
                    ...((noCode?.target_column || noCode?.target_name) ? { targetColumn: noCode.target_column || noCode.target_name } : {}),
                    ...((noCode?.feature_columns || []).length > 0
                      ? { featureColumns: noCode.feature_columns.join(', ') }
                      : {}),
                  }));
                }}
                fullWidth
                helperText="Select the model blueprint — architecture is defined in Model Registry"
              >
                {(assetsQuery.data?.results || []).map((asset) => (
                  <MenuItem key={asset.id} value={String(asset.id)}>
                    {asset.name} ({asset.family} / {asset.framework})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Dataset version"
                value={jobForm.datasetVersionId}
                onChange={(event) =>
                  setJobForm((current) => ({
                    ...current,
                    datasetVersionId: event.target.value,
                  }))
                }
                fullWidth
                helperText="Select the dataset version for training"
              >
                {(datasetVersionsQuery.data?.results || []).map((version) => (
                  <MenuItem key={version.id} value={String(version.id)}>
                    {version.dataset_name} / v{version.version_number} / {version.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            {/* ── Architecture Summary (read-only from Model Asset) ── */}
            {(() => {
              const selectedAsset = (assetsQuery.data?.results || []).find((a) => String(a.id) === jobForm.modelAssetId);
              const arch = selectedAsset?.runtime_spec?.authoring?.no_code?.architecture;
              if (!selectedAsset) return null;
              if (!arch) {
                return (
                  <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                    No architecture defined on <strong>{selectedAsset.name}</strong>.
                    Go to <strong>Model Registry</strong> to define the architecture canvas before creating a training job.
                  </Alert>
                );
              }
              const modelType = arch.model_type || 'single';
              const algKey = arch.single_estimator?.algorithm || '';
              const algLabel = ALGORITHM_LABELS[algKey] || algKey;
              const hyperParams = arch.single_estimator?.params || {};
              const preprocessing = arch.preprocessing || [];
              const baseEstimators = arch.base_estimators || [];
              const metaLearner = arch.meta_learner;
              return (
                <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="overline" color="text.secondary">
                      Architecture from {selectedAsset.name}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      <Chip size="small" label={modelType === 'single' ? 'Single Model' : modelType === 'voting' ? 'Voting Ensemble' : 'Stacking'} color="primary" />
                      {modelType === 'single' && algLabel && (
                        <Chip size="small" label={algLabel} />
                      )}
                      {modelType !== 'single' && baseEstimators.map((e: any, i: number) => (
                        <Chip key={i} size="small" label={ALGORITHM_LABELS[e.algorithm] || e.algorithm} variant="outlined" />
                      ))}
                      {modelType === 'stacking' && metaLearner && (
                        <Chip size="small" label={`Meta: ${ALGORITHM_LABELS[metaLearner.algorithm] || metaLearner.algorithm}`} color="secondary" />
                      )}
                      {modelType === 'voting' && (
                        <Chip size="small" label={`Strategy: ${arch.voting_strategy || 'soft'}`} variant="outlined" />
                      )}
                      {preprocessing.length > 0 && preprocessing.map((step: any, i: number) => (
                        <Chip key={i} size="small" label={step.type} variant="outlined" color="info" />
                      ))}
                    </Stack>
                    {modelType === 'single' && Object.keys(hyperParams).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Hyperparameters</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {Object.entries(hyperParams).map(([k, v]) => (
                            <Chip key={k} size="small" label={`${k}: ${v}`} variant="outlined" color="primary" />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      To change the architecture, edit this asset in Model Registry.
                    </Typography>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Feature Set selector */}
            <TextField
              select
              label="Feature Set (optional)"
              value={jobForm.featureSetId}
              onChange={(event) => {
                const fsId = event.target.value;
                const fs = (featureSetsQuery.data?.results || []).find((f) => String(f.id) === fsId);
                const def = fs?.definition || {};
                setJobForm((current) => ({
                  ...current,
                  featureSetId: fsId,
                  ...(fs ? {
                    featureColumns: (def.feature_columns || []).join(', '),
                    targetColumn: def.target_column || current.targetColumn,
                  } : {}),
                }));
              }}
              fullWidth
              helperText="Auto-fill feature and target columns from a saved feature set"
            >
              <MenuItem value="">None (manual entry)</MenuItem>
              {(featureSetsQuery.data?.results || []).map((fs) => (
                <MenuItem key={fs.id} value={String(fs.id)}>
                  {fs.name} ({(fs.definition?.feature_columns || []).length} features)
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Target column"
                value={jobForm.targetColumn}
                onChange={(event) =>
                  setJobForm((current) => ({
                    ...current,
                    targetColumn: event.target.value,
                  }))
                }
                fullWidth
                helperText="Column to predict"
              />
              <TextField
                label="Feature columns"
                helperText="Comma separated. Leave empty to infer automatically."
                value={jobForm.featureColumns}
                onChange={(event) =>
                  setJobForm((current) => ({
                    ...current,
                    featureColumns: event.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>

            {/* ── Compute Spec ── */}
            <ComputeSpecSelector
              value={jobForm.computeSpecId}
              onChange={(specId) =>
                setJobForm((current) => ({ ...current, computeSpecId: specId }))
              }
              jobType={editingJobId ? 'training' : undefined}
              jobId={editingJobId ?? undefined}
              size="small"
            />

            <Divider />

            {/* ── Section: Advanced ── */}
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5 }}>
              Evaluation & Advanced
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                  Test size: {jobForm.testSize}
                </Typography>
                <Slider
                  value={Number(jobForm.testSize)}
                  onChange={(_, val) =>
                    setJobForm((current) => ({
                      ...current,
                      testSize: String(val),
                    }))
                  }
                  min={0.05}
                  max={0.5}
                  step={0.05}
                  marks={[
                    { value: 0.1, label: '10%' },
                    { value: 0.2, label: '20%' },
                    { value: 0.3, label: '30%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${(v * 100).toFixed(0)}%`}
                />
              </Box>
              <TextField
                label="Random state"
                value={jobForm.randomState}
                onChange={(event) =>
                  setJobForm((current) => ({
                    ...current,
                    randomState: event.target.value,
                  }))
                }
                fullWidth
                helperText="Seed for reproducibility"
                sx={{ flex: 0.5 }}
              />
              <TextField
                label="Cross-validation folds"
                helperText="0 = disabled, 3–10 recommended"
                type="number"
                value={jobForm.cvFolds}
                onChange={(event) =>
                  setJobForm((current) => ({
                    ...current,
                    cvFolds: event.target.value,
                  }))
                }
                inputProps={{ min: 0, max: 20 }}
                fullWidth
                sx={{ flex: 0.5 }}
              />
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={jobForm.autoLaunch}
                  onChange={(event) =>
                    setJobForm((current) => ({
                      ...current,
                      autoLaunch: event.target.checked,
                    }))
                  }
                />
              }
              label={editingJobId ? 'Launch immediately after saving' : 'Launch immediately after creation'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setEditingJobId(null); setJobForm(initialTrainingForm); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (editingJobId) {
                updateJobMutation.mutate({ id: editingJobId, form: jobForm });
              } else {
                createJobMutation.mutate(jobForm);
              }
            }}
            disabled={
              !jobForm.modelAssetId ||
              !jobForm.datasetVersionId ||
              !jobForm.targetColumn ||
              createJobMutation.isPending ||
              updateJobMutation.isPending
            }
          >
            {editingJobId
              ? (updateJobMutation.isPending ? 'Saving...' : 'Save Changes')
              : (createJobMutation.isPending ? 'Creating...' : 'Create Job')
            }
          </Button>
        </DialogActions>
      </Dialog>

      <TrainingJobDetailsDialog
        job={selectedJob}
        open={Boolean(selectedJob)}
        onClose={() => { setLogDialogJobId(null); setInspectTab(0); }}
      />

      <Dialog
        open={false}
        onClose={() => { setLogDialogJobId(null); setInspectTab(0); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Job Inspector
          {selectedJob && (
            <Typography variant="body2" color="text.secondary">
              {selectedJob.model_asset_name} &middot; {selectedJob.dataset_version_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              {/* Configuration summary */}
              {selectedJob.config && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="overline" color="text.secondary">Configuration</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {(selectedJob.config as any)?.model_type && (selectedJob.config as any).model_type !== 'single' && (
                        <Chip size="small" label={`Model: ${(selectedJob.config as any).model_type}`} color="secondary" />
                      )}
                      {(selectedJob.config as any)?.algorithm && (
                        <Chip size="small" label={`Algorithm: ${(selectedJob.config as any).algorithm}`} />
                      )}
                      {(selectedJob.config as any)?.preprocessing?.scaler && (
                        <Chip size="small" label={`Scaler: ${(selectedJob.config as any).preprocessing.scaler}`} variant="outlined" />
                      )}
                      {(selectedJob.config as any)?.preprocessing?.feature_selection && (
                        <Chip size="small" label={`Feature Sel: ${(selectedJob.config as any).preprocessing.feature_selection}`} variant="outlined" />
                      )}
                      {(selectedJob.config as any)?.test_size != null && (
                        <Chip size="small" label={`Test size: ${(selectedJob.config as any).test_size}`} variant="outlined" />
                      )}
                      {(selectedJob.config as any)?.cv_folds != null && Number((selectedJob.config as any).cv_folds) > 0 && (
                        <Chip size="small" label={`CV folds: ${(selectedJob.config as any).cv_folds}`} variant="outlined" />
                      )}
                      {(selectedJob.config as any)?.executor && (
                        <Chip size="small" label={`Executor: ${(selectedJob.config as any).executor}`} variant="outlined" />
                      )}
                      {(selectedJob as any).runtime_spec?.compute_mode && (
                        <Chip
                          size="small"
                          label={`Compute: ${computeModeLabel(selectedJob)}`}
                          variant="outlined"
                          color={(selectedJob as any).runtime_spec.compute_mode === 'remote_vm' ? 'warning' : 'default'}
                        />
                      )}
                    </Stack>
                    {(selectedJob.config as any)?.hyperparameters &&
                      Object.keys((selectedJob.config as any).hyperparameters).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">Hyperparameters</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          {Object.entries((selectedJob.config as any).hyperparameters).map(([k, v]) => (
                            <Chip key={k} size="small" label={`${k}: ${v}`} variant="outlined" color="primary" />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}

              <Tabs value={inspectTab} onChange={(_, v) => setInspectTab(v)} sx={{ mb: 2 }}>
                <Tab label="Logs" />
                <Tab label="Metrics &amp; Charts" disabled={selectedJob.status !== 'COMPLETED'} />
                <Tab label="Resource Usage" disabled={selectedJob.status !== 'COMPLETED'} />
                <Tab label="Telemetry" />
              </Tabs>

              {/* ── Tab 0: Logs ── */}
              {inspectTab === 0 && (
                <Stack spacing={2}>
                  {/* Live training progress panel */}
                  {(selectedJob.status === 'RUNNING' || selectedJob.status === 'QUEUED') && (() => {
                    const progress = (selectedJob as any).progress || {};
                    const currentEpoch = progress.current_epoch;
                    const totalEpochs = progress.total_epochs;
                    const lossHistory = progress.loss_history as number[] | undefined;
                    const phase = progress.phase || (selectedJob.status === 'QUEUED' ? 'queued' : 'running');

                    return (
                      <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>Live Training Progress</Typography>
                          {currentEpoch && totalEpochs ? (
                            <Stack spacing={1.5}>
                              <Stack direction="row" spacing={3} alignItems="center">
                                <Box sx={{ flex: 1 }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">
                                      Epoch {currentEpoch} / {totalEpochs}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {Math.round((currentEpoch / totalEpochs) * 100)}%
                                    </Typography>
                                  </Stack>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(currentEpoch / totalEpochs) * 100}
                                    sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
                                  />
                                </Box>
                              </Stack>
                              <Stack direction="row" spacing={2}>
                                {progress.loss != null && (
                                  <Chip size="small" label={`Loss: ${progress.loss.toFixed(6)}`} variant="outlined" />
                                )}
                                {progress.best_loss != null && (
                                  <Chip size="small" label={`Best: ${progress.best_loss.toFixed(6)}`} variant="outlined" color="success" />
                                )}
                                {progress.lr != null && (
                                  <Chip size="small" label={`LR: ${progress.lr}`} variant="outlined" />
                                )}
                              </Stack>
                              {lossHistory && lossHistory.length > 1 && (
                                <LossChart lossHistory={lossHistory} title="Loss Curve (Live)" />
                              )}
                            </Stack>
                          ) : (
                            <Stack spacing={1}>
                              <Typography variant="body2" color="text.secondary">
                                {phase === 'fitting' ? `Fitting ${progress.algorithm || 'model'}...` : 'Preparing data & model...'}
                              </Typography>
                              <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()}
                  <TextField
                    value={selectedJob.logs || selectedJob.error_message || 'No logs yet.'}
                    multiline
                    minRows={14}
                    fullWidth
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
                  />
                </Stack>
              )}

              {/* ── Tab 1: Metrics & Charts ── */}
              {inspectTab === 1 && selectedJob.metrics && (
                <Stack spacing={3}>
                  {/* Scalar metrics summary */}
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>Scalar Metrics</Typography>
                      <Grid container spacing={2}>
                        {Object.entries(selectedJob.metrics)
                          .filter(([k]) => typeof selectedJob.metrics[k] === 'number')
                          .map(([key, val]) => (
                            <Grid size={{ xs: 6, sm: 3 }} key={key}>
                              <Typography variant="caption" color="text.secondary">{key}</Typography>
                              <Typography variant="h6">
                                {typeof val === 'number' ? val.toFixed(4) : String(val)}
                              </Typography>
                            </Grid>
                          ))}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Cross-validation results */}
                  {selectedJob.metrics.cross_validation && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>Cross-Validation</Typography>
                        <Grid container spacing={2}>
                          <Grid size={4}>
                            <Typography variant="caption" color="text.secondary">Mean</Typography>
                            <Typography variant="h6">
                              {(selectedJob.metrics.cross_validation as any).mean?.toFixed(4)}
                            </Typography>
                          </Grid>
                          <Grid size={4}>
                            <Typography variant="caption" color="text.secondary">Std</Typography>
                            <Typography variant="h6">
                              {(selectedJob.metrics.cross_validation as any).std?.toFixed(4)}
                            </Typography>
                          </Grid>
                          <Grid size={4}>
                            <Typography variant="caption" color="text.secondary">Folds</Typography>
                            <Typography variant="h6">
                              {(selectedJob.metrics.cross_validation as any).scores?.length ?? '—'}
                            </Typography>
                          </Grid>
                        </Grid>
                        {(selectedJob.metrics.cross_validation as any).scores && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Per-fold scores:</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                              {((selectedJob.metrics.cross_validation as any).scores as number[]).map(
                                (s: number, i: number) => (
                                  <Chip key={i} size="small" label={`Fold ${i + 1}: ${s.toFixed(4)}`} variant="outlined" />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  <Divider />

                  {/* Chart panels */}
                  <Grid container spacing={3}>
                    {selectedJob.metrics.confusion_matrix && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Confusion Matrix</Typography>
                            <ConfusionMatrix data={selectedJob.metrics.confusion_matrix as any} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {selectedJob.metrics.roc_curve && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>ROC Curve</Typography>
                            <ROCCurve data={selectedJob.metrics.roc_curve as any} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {selectedJob.metrics.pr_curve && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Precision-Recall Curve</Typography>
                            <PRCurve data={selectedJob.metrics.pr_curve as any} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {selectedJob.metrics.feature_importance &&
                      Array.isArray(selectedJob.metrics.feature_importance) &&
                      (selectedJob.metrics.feature_importance as any[]).length > 0 && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Feature Importance</Typography>
                            <FeatureImportance data={selectedJob.metrics.feature_importance as any} />
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              )}

              {/* ── Tab 2: Resource Usage ── */}
              {inspectTab === 2 && (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>Training Resource Usage</Typography>
                    <Grid container spacing={2}>
                      {selectedJob.metrics?.resource_usage ? (
                        <>
                          {(selectedJob.metrics.resource_usage as any).training_time_sec != null && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <TimerIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">Training Time</Typography>
                              </Stack>
                              <Typography variant="h6">
                                {((selectedJob.metrics.resource_usage as any).training_time_sec as number).toFixed(2)}s
                              </Typography>
                            </Grid>
                          )}
                          {(selectedJob.metrics.resource_usage as any).dataset_rows != null && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Dataset Rows</Typography>
                              <Typography variant="h6">
                                {(selectedJob.metrics.resource_usage as any).dataset_rows}
                              </Typography>
                            </Grid>
                          )}
                          {(selectedJob.metrics.resource_usage as any).feature_count != null && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Feature Count</Typography>
                              <Typography variant="h6">
                                {(selectedJob.metrics.resource_usage as any).feature_count}
                              </Typography>
                            </Grid>
                          )}
                          {(selectedJob.metrics.resource_usage as any).train_rows != null && (
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <Typography variant="caption" color="text.secondary">Train / Test Split</Typography>
                              <Typography variant="h6">
                                {(selectedJob.metrics.resource_usage as any).train_rows} / {(selectedJob.metrics.resource_usage as any).test_rows}
                              </Typography>
                            </Grid>
                          )}
                        </>
                      ) : (
                        <Grid size={12}>
                          <Typography variant="body2" color="text.secondary">
                            No resource usage data recorded for this job.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {inspectTab === 3 && (
                <Stack spacing={2}>
                  {telemetryQuery.isFetching && <LinearProgress />}
                  {telemetryQuery.error && (
                    <Alert severity="error">{getApiErrorMessage(telemetryQuery.error, 'Failed to load telemetry.')}</Alert>
                  )}
                  {telemetryQuery.data ? (() => {
                    const telemetry = telemetryQuery.data;
                    const lossSeries = telemetry.series.find((series) => series.metric === 'loss');
                    const lossHistory = (lossSeries?.points || [])
                      .map((point) => point.value)
                      .filter((value): value is number => typeof value === 'number');
                    const scalarSeries = telemetry.series
                      .filter((series) => series.latest != null)
                      .sort((a, b) => a.metric.localeCompare(b.metric));
                    return (
                      <>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Telemetry Summary</Typography>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">Events</Typography>
                                <Typography variant="h6">{telemetry.summary.event_count}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">Metrics</Typography>
                                <Typography variant="h6">{telemetry.summary.metric_count}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">Loss curve</Typography>
                                <Typography variant="h6">{telemetry.summary.has_loss_curve ? 'Yes' : 'No'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 6, sm: 3 }}>
                                <Typography variant="caption" color="text.secondary">Gradient stats</Typography>
                                <Typography variant="h6">{telemetry.summary.has_gradient_stats ? 'Yes' : 'No'}</Typography>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>

                        {lossHistory.length > 1 && (
                          <LossChart lossHistory={lossHistory} title="Training Loss Telemetry" />
                        )}

                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>Scalar Series</Typography>
                            <Grid container spacing={2}>
                              {scalarSeries.slice(0, 24).map((series) => (
                                <Grid size={{ xs: 6, md: 3 }} key={series.metric}>
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {series.metric}
                                  </Typography>
                                  <Typography variant="h6">
                                    {series.latest != null ? Number(series.latest).toFixed(5) : 'n/a'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {series.points.length} point{series.points.length === 1 ? '' : 's'}
                                  </Typography>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </>
                    );
                  })() : (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          No telemetry has been recorded for this job yet.
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Stack>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLogDialogJobId(null); setInspectTab(0); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(jobToDelete)}
        title="Delete training job"
        message={`Delete training job for ${jobToDelete?.name}? This cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onCancel={() => setJobToDelete(null)}
        onConfirm={() => {
          if (jobToDelete) {
            deleteMutation.mutate(jobToDelete.id);
          }
        }}
      />
    </Box>
  );
};

export default ModelLabTrainingJobsPage;
